import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRoster } from '../core/api';
import { useT } from '../core/lexicon';
import { guessesComplete, takenGuesses } from '../core/ballot';
import { seq } from '../core/util';
import { Avatar } from '../core/sprite';
import { useAsync } from '../core/useAsync';
import { useApp } from '../App';
import { useVoting } from './useVoting';
import { ListenLink, VotingClosed, VotingLoading, votingRight } from './ballotShared';
import { Screen, TopBar } from './Layout';
import type { RosterMember } from '../core/types';

/**
 * Guess who submitted each track. Bijection enforced (rule 6, spec §20): everyone
 * submitted one, so each name is used at most once — a used name is greyed with
 * the track it went on. The song is the only clue; no bios or phrases appear here.
 *
 * Candidates are the active roster (GET /api/roster) minus the caller, with real
 * sprites when set.
 */
export function Guess() {
  const t = useT();
  const nav = useNavigate();
  const { user } = useApp();
  const v = useVoting();
  const roster = useAsync<RosterMember[]>(getRoster, []);
  const [active, setActive] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const candidates = useMemo<RosterMember[]>(
    () =>
      (roster.data ?? [])
        .filter((m) => m.user_id !== user.user_id)
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [roster.data, user.user_id],
  );
  const byId = useMemo(() => new Map(candidates.map((c) => [c.user_id, c])), [candidates]);

  const title = t('phase.guess.name');
  if (v.loading) return <VotingLoading title={title} />;
  if (!v.round || v.round.phase !== 'VOTING') return <VotingClosed t={t} title={title} round={v.round} />;

  const { draft } = v;
  const named = [...draft.values()].filter((d) => d.guessed_user_id != null).length;
  const total = v.votable.length;

  async function send() {
    setBusy(true);
    setProblem(null);
    setNote(null);
    const res = await v.save();
    if (res.error) {
      setProblem(res.error);
      setBusy(false);
    } else if (res.submitted) {
      nav('/');
    } else {
      setNote(t('ballot.draft'));
      setBusy(false);
    }
  }

  const activeTrack = active != null ? v.votable.find((tr) => tr.submission_id === active) : null;

  return (
    <Screen>
      <TopBar title={t('round.label', { n: v.round.sequence_no })} sub={title} right={votingRight(v.round)} />
      <ListenLink t={t} round={v.round} />

      <section className="prog" aria-live="polite">
        <div className="ph">
          <span className="pl">{t('phase.guess.name').toUpperCase()}</span>
          <span className="pc">
            {named}/{total}
          </span>
        </div>
        <div className="pips">
          {Array.from({ length: total }, (_, i) => (
            <div key={i} className={`pip${i < named ? ' in' : ''}`} />
          ))}
        </div>
        <p className="g-hint">{t('phase.guess.rule')}</p>
      </section>

      <div className="list">
        {v.votable.map((tr) => {
          const g = draft.get(tr.submission_id)?.guessed_user_id ?? null;
          const member = g != null ? byId.get(g) : undefined;
          return (
            <button
              key={tr.submission_id}
              className={`gtrack${g != null ? ' named' : ''}`}
              onClick={() => setActive(tr.submission_id)}
            >
              <span className="tn">{seq(tr.display_order)}</span>
              <span>
                <span className="tt">{tr.title}</span>
                <span className="ta">{tr.subtitle ?? ''}</span>
              </span>
              <span className="slot">
                {member == null ? (
                  <span className="se">?</span>
                ) : (
                  <>
                    <Avatar spriteData={member.sprite_data} seed={member.sprite_key || String(member.user_id)} size={36} />
                    <span className="who">{shortName(member.display_name)}</span>
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="act">
        {problem ? <div className="errline" style={{ marginBottom: 8 }}>{problem}</div> : null}
        {note ? <div className="errline" style={{ marginBottom: 8, background: 'transparent', color: 'var(--k1)' }}>{note}</div> : null}
        <button className="go" disabled={!guessesComplete(draft) || named !== total || busy} onClick={send}>
          {busy ? '…' : guessesComplete(draft) && named === total ? t('phase.guess.cta').toUpperCase() : t('phase.guess.remaining', { n: total - named }).toUpperCase()}
        </button>
      </div>

      {activeTrack ? (
        <>
          <div className="scrim" onClick={() => setActive(null)} />
          <section className="sheet" role="dialog" aria-modal="true">
            <div className="sh">
              <div>
                <div className="sht">{t('phase.guess.prompt').toUpperCase()}</div>
                <div className="shs">
                  {seq(activeTrack.display_order)} · {activeTrack.title.toUpperCase()}
                </div>
              </div>
              <button className="close" onClick={() => setActive(null)}>
                X
              </button>
            </div>
            <div className="people">
              {candidates.map((c) => {
                const taken = takenGuesses(draft, activeTrack.submission_id).has(c.user_id);
                const heldOn = [...draft.entries()].find(([, d]) => d.guessed_user_id === c.user_id)?.[0];
                const trackNo = heldOn ? v.votable.find((tr) => tr.submission_id === heldOn)?.display_order : undefined;
                return (
                  <button
                    key={c.user_id}
                    className="person"
                    disabled={taken}
                    onClick={() => {
                      v.update(activeTrack.submission_id, { guessed_user_id: c.user_id });
                      setActive(null);
                    }}
                  >
                    <Avatar spriteData={c.sprite_data} seed={c.sprite_key || String(c.user_id)} size={44} />
                    <span>
                      <span className="pn">{c.display_name}</span>
                      {taken && trackNo != null ? (
                        <span className="ph2">{t('phase.guess.reused', { n: seq(trackNo) })}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </Screen>
  );
}

const shortName = (name: string): string => name.split(' ').pop()?.toUpperCase() ?? '';
